import React, { useContext, useState, useEffect } from 'react';
import c from './search.module.scss';
import { SearchContext } from '../../providers/search-provider/SearchProvider';
import PageContainer from '../../layouts/page-container/PageContainer';
import {
  FilterDropdown,
  LoadingIndicator,
  useMedia,
} from '@monash/portal-react';
import SearchResultsCard from '../../ui/search-result-card/SearchResultsCard';
import filterLinksByCategory from '../../utilities/filter-and-sort/filterLinksByCategory';
import dummyLinks from '../../utilities/data/dummy-links.json';
import { AccessibilityContext } from '@monash/portal-frontend-common';
import SearchBar from '../../utilities/search/SearchBar';
import SearchError from './SearchError';
import { getSearchResultLiveMsg } from './search-utils';

export const Search = () => {
  const {
    allCategories,
    runSearchCallCount,
    loadingLinks,
    searchTarget,
    searchResult,
    searchError,
  } = useContext(SearchContext);
  const pageSize = useMedia();
  const { setAppLivePoliteMsg, resetAppLiveMsgs } =
    useContext(AccessibilityContext);

  // Create keyword map from search results with unique keywords
  const suggestedKeywordsMap = new Map();
  const searchTerms = [
    ...new Set(searchTarget?.trim().toLowerCase().split(' ')),
  ];

  searchResult?.forEach((i) => {
    const uniqueKeywords = [...new Set(Object.values(i.keywords))];
    uniqueKeywords.forEach((keyword, i) => {
      // Count total number of each unique keyword from search result
      const count = suggestedKeywordsMap.get(keyword);
      if (searchTerms.includes(keyword)) {
        uniqueKeywords.splice(i, 1);
      } else if (!count) {
        suggestedKeywordsMap.set(keyword, 1);
      } else {
        suggestedKeywordsMap.set(keyword, count + 1);
      }
    });
  });

  // Filters and sorting
  const categoryFilters = allCategories.map((category) => ({
    text: category.title,
    value: category.id,
  }));

  const [selectedCategories, setSelectedCategories] = useState([]);

  // Display links
  const displayAllLinks = searchResult;
  const filteredLinks = filterLinksByCategory(
    selectedCategories,
    displayAllLinks
  );

  const displayLinks = loadingLinks
    ? dummyLinks
    : !selectedCategories.length
    ? displayAllLinks
    : filteredLinks;

  const resultTotal = (searchResult) => {
    let totalText = ' results';
    if (searchResult.length <= 1) {
      totalText = ' result';
    }
    return displayLinks.length + totalText;
  };

  // Note: have a short dependency list to make sure live msg is only updated when we need to
  // - loadingLinks: to help reset live region when same search executed continuously
  // - runSearchCallCount: to only update live region when each search API call is resolved
  useEffect(() => {
    resetAppLiveMsgs();
    setAppLivePoliteMsg(
      getSearchResultLiveMsg({
        isLoading: loadingLinks,
        query: searchTarget,
        error: searchError,
        resultCount: searchResult?.length,
      })
    );
  }, [loadingLinks, runSearchCallCount]);

  return (
    <PageContainer type="default">
      {pageSize === 'S' && <SearchBar />}
      {loadingLinks && (
        <div className={c.loading}>
          <LoadingIndicator />
        </div>
      )}
      {!loadingLinks && searchError && <SearchError />}
      {!loadingLinks && !searchError && (
        <div tabIndex="-1" className={c.searchResult}>
          <div className={c.header}>
            <div tabIndex="-1" role="status">
              {resultTotal(searchResult)}
            </div>
            <div className={c.filters}>
              <FilterDropdown
                placeholder="All categories"
                filters={categoryFilters}
                selectedFilter={selectedCategories}
                setSelectedFilter={setSelectedCategories}
                multi
              />
            </div>
          </div>
          <div className={c.links} id="searchResult">
            {displayLinks?.map((item, i) => (
              <SearchResultsCard
                entity={item.entityContent}
                key={i}
                index={i}
              />
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
